/**
 * Period-grain participation helpers — week buckets + launch-window coverage.
 */

import type {
  ParticipationPeriodCommitment,
  ParticipationRequestStatus,
  TeamParticipation,
} from './planningEngineModule.types';

/** Monday-aligned ISO week start for a date. */
export function weekStartIso(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00Z');
  const day = d.getUTCDay(); // 0 Sun … 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

export function addDaysIso(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Expand an inclusive launch window into weekly buckets. */
export function expandWindowToWeekBuckets(
  start?: string | null,
  end?: string | null,
): { bucketStart: string; bucketEnd: string }[] {
  if (!start && !end) return [];
  const from = weekStartIso(start ?? end!);
  const to = end ?? start!;
  const buckets: { bucketStart: string; bucketEnd: string }[] = [];
  let cur = from;
  // Cap to avoid runaway ranges
  for (let i = 0; i < 52; i++) {
    const bucketEnd = addDaysIso(cur, 6);
    buckets.push({ bucketStart: cur, bucketEnd });
    if (cur >= weekStartIso(to)) break;
    cur = addDaysIso(cur, 7);
  }
  return buckets;
}

export function fillPeriodCommitmentsFromWindow(
  start?: string | null,
  end?: string | null,
  status: ParticipationRequestStatus = 'requested',
  hoursPerWeek?: number | null,
): ParticipationPeriodCommitment[] {
  return expandWindowToWeekBuckets(start, end).map((b) => ({
    bucketStart: b.bucketStart,
    bucketEnd: b.bucketEnd,
    hours: hoursPerWeek ?? null,
    status,
  }));
}

export function rollupRequestStatus(
  periods: ParticipationPeriodCommitment[],
  fallback: ParticipationRequestStatus = 'requested',
): ParticipationRequestStatus {
  if (periods.length === 0) return fallback;
  if (periods.every((p) => p.status === 'confirmed')) return 'confirmed';
  if (periods.every((p) => p.status === 'declined')) return 'declined';
  if (periods.some((p) => p.status === 'confirmed' || p.status === 'tentative')) {
    return 'tentative';
  }
  return 'requested';
}

/**
 * True when every week overlapping the launch window has status === confirmed.
 * Empty period list → caller should fall back to whole-row committed flag.
 */
export function periodsCoverLaunchWindow(
  periods: ParticipationPeriodCommitment[],
  launchStart?: string | null,
  launchEnd?: string | null,
): { covered: boolean; missingBuckets: string[]; unconfirmedBuckets: string[] } {
  const needed = expandWindowToWeekBuckets(launchStart, launchEnd);
  if (needed.length === 0) {
    return { covered: periods.length === 0 || periods.every((p) => p.status === 'confirmed'), missingBuckets: [], unconfirmedBuckets: [] };
  }
  const byStart = new Map(periods.map((p) => [weekStartIso(p.bucketStart), p]));
  const missingBuckets: string[] = [];
  const unconfirmedBuckets: string[] = [];
  for (const n of needed) {
    const row = byStart.get(n.bucketStart);
    if (!row) {
      missingBuckets.push(n.bucketStart);
      continue;
    }
    if (row.status !== 'confirmed') unconfirmedBuckets.push(n.bucketStart);
  }
  return {
    covered: missingBuckets.length === 0 && unconfirmedBuckets.length === 0,
    missingBuckets,
    unconfirmedBuckets,
  };
}

/** Soft-stale confirmed periods → tentative (calendar change reconfirm). */
export function staleConfirmedPeriods(
  periods: ParticipationPeriodCommitment[],
): ParticipationPeriodCommitment[] {
  return periods.map((p) =>
    p.status === 'confirmed' ? { ...p, status: 'tentative' as const } : p,
  );
}

export function participationUsesPeriodGrain(row: TeamParticipation): boolean {
  return (row.periodCommitments?.length ?? 0) > 0;
}
